import type { RunCardApiCard } from '../../lib/api.js';
import type { RunCardData } from './RunCard.js';

/*
 * Moved out of Bookings.tsx for Ted Item 34: the check-in print prompt
 * (useRunCardPrompt) builds the same cards from the same server rows, and a
 * second copy of this rename would be a second truth waiting to drift.
 */
// #549 — map one server run-card row (snake_case API shape) to the
// RunCard component's props. All display derivation (age, sex/altered,
// stay-window, day-grid columns/rows) happens inside the component via the
// pure `run-card-grid` helpers — this is a straight field rename.
export function apiCardToRunCard(c: RunCardApiCard): RunCardData {
  return {
    cardId: c.card_id,
    bookingId: c.booking_id,
    dogId: c.dog_id,
    dogName: c.dog_name,
    ownerName: c.owner_name,
    ownerPhone: c.owner_phone,
    photoUrl: c.photo_url,
    breed: c.breed,
    birthDateISO: c.birth_date,
    sex: c.sex,
    alteredStatus: c.altered_status,
    isNeutered: c.is_neutered,
    temperament: c.temperament,
    serviceLabel: c.service_label,
    dogIndex: c.dog_index,
    dogCount: c.dog_count,
    stayStartISO: c.stay_start,
    stayEndISO: c.stay_end,
    dropOffTime: c.drop_off_time,
    pickUpTime: c.pick_up_time,
    // Sawyer 06 beds-lockers-runcard (B5) — absent on an older server ⇒ both boxes print blank.
    placement: c.placement
      ? { bedName: c.placement.bed?.name ?? null, locker: c.placement.locker ?? null }
      : undefined,
    feeding: {
      method: c.feeding.method,
      foodType: c.feeding.food_type,
      notes: c.feeding.notes,
    },
    // #716 — `?? []` / `?? null` are deploy-order safety, not defensive
    // habit: frontends auto-deploy on merge and the SERVER does not, so
    // these fields are legitimately absent until the Railway deploy lands.
    // Absent ⇒ the card prints the blank-line stationery fallback, which is
    // exactly its pre-#716 behaviour.
    belongings: c.belongings ?? [],
    feedingOverride: c.feeding.override ?? null,
    careVerifiedAtISO: c.care_verified_at ?? null,
    medications: c.medications.map((m) => ({
      id: m.id,
      name: m.name,
      dosage: m.dosage,
      frequency: m.frequency,
      // Sawyer 08 meds-feeding (A1) — absent on an older server ⇒ frequency decides, as before.
      slots: m.slots ?? [],
      instructions: m.instructions,
      // Sawyer 10 customer-booking-verify-policy (C6) — LOCKBOX tag on the medication row.
      ...(m.lockbox === true ? { lockbox: true } : {}),
    })),
    medicationNotesText: c.medication_notes,
    // Ted #28 Phase 2 (#1050) — absent on a server older than Phase 2.
    flags: c.flags ?? [],
    // #597 / B7 — absent on a server older than #597 ⇒ no allergy line, no
    // Gingr caption (the pre-#597 card).
    allergies: c.allergies ?? null,
    gingrCare: {
      medicationImportedAtISO: c.gingr_care?.medication_imported_at ?? null,
      feedingImportedAtISO: c.gingr_care?.feeding_imported_at ?? null,
    },
    // Ted #46 (#1218) — all absent on a server older than #1218 ⇒ no behavior
    // notes, no this-stay section, and the card is treated as live.
    behaviorNotes: c.behavior_notes ?? null,
    stayEdits: c.stay_edits
      ? {
          note: c.stay_edits.note ?? null,
          fields: c.stay_edits.fields ?? [],
          overridden: c.stay_edits.overridden ?? [],
        }
      : undefined,
    visitRecord:
      c.source === 'visit_record' && c.printed_at
        ? { printedAtISO: c.printed_at, changedSincePrint: c.changed_since_print === true }
        : null,
  };
}
